'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * 단추 하나에만 해당하는 설명(주로 「왜 지금 못 누르나」)을 그 단추 위 말풍선으로 띄운다.
 *
 * 화면에 고정 문장으로 두면 늘 자리를 차지하고, 그 문장이 어느 단추 얘기인지 따로 읽어야
 * 한다(2026-09-22 사용자: 「삭제 버튼 위에 툴팁으로」). 규칙은 AGENTS.md §Frontend.
 *
 * - 마우스: 1초 머물면 뜨고 떠나면 진다. 지나가기만 해도 뜨면 화면이 어지럽다(2026-10-05 사용자 「1초 정도 올려두면」).
 * - 터치: 누르면 뜨고, 몇 초 뒤나 다른 곳을 누르면 진다 - 모바일에는 hover 가 없다.
 * - 비활성 단추는 이벤트를 받지 않으므로 이 겉 칸이 받는다(안쪽 disabled 는 포인터를 통과시킨다).
 * - 말풍선은 `fixed` 로 뜬다. 줄 안에 끼어들지 않으니 아무것도 밀리지 않는다(§1.6).
 *
 * `reason` 이 null 이면 아무것도 하지 않는다 - 겉 칸만 남아 자리는 같다.
 */
const HOVER_DELAY_MS = 1000;

export function ReasonTip({
  reason,
  children,
  className,
}: {
  reason: string | null | undefined;
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const tipId = useId();
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function show() {
    const box = ref.current?.getBoundingClientRect();
    if (!reason || !box) return;
    // 가운데를 단추에 맞추되 화면 끝을 넘지 않게 민다 - 좁은 화면 끝 단추의 말풍선이 잘리지 않게.
    const half = Math.min(160, (window.innerWidth - 32) / 2);
    const center = box.left + box.width / 2;
    setAt({ left: Math.min(Math.max(center, 16 + half), window.innerWidth - 16 - half), top: box.top });
  }
  function hide() {
    if (timer.current) clearTimeout(timer.current);
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setAt(null);
  }

  // 터치로 띄운 것은 다른 곳을 누르거나 스크롤하면 닫는다.
  useEffect(() => {
    if (!at) return;
    const close = (event: Event) => {
      if (event.target instanceof Node && ref.current?.contains(event.target)) return;
      hide();
    };
    document.addEventListener('pointerdown', close);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', hide, true);
    };
  }, [at]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  }, []);

  return (
    <span
      ref={ref}
      tabIndex={reason ? 0 : undefined}
      aria-describedby={reason && at ? tipId : undefined}
      className={cn('inline-flex', reason && '[&_button:disabled]:pointer-events-none', className)}
      onFocus={show}
      onBlur={hide}
      onKeyDown={(event) => { if (event.key === 'Escape') hide(); }}
      onPointerEnter={(event) => {
        if (event.pointerType !== 'mouse' || !reason) return;
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(show, HOVER_DELAY_MS);
      }}
      onPointerLeave={(event) => event.pointerType === 'mouse' && hide()}
      onClick={() => {
        if (!reason) return;
        show();
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => setAt(null), 4000);
      }}
    >
      {children}
      {/* 사유가 사라지면(고른 줄이 바뀌면) 떠 있던 말풍선도 같이 사라진다. */}
      {reason && at && createPortal(
        <span
          id={tipId}
          role="tooltip"
          style={{ left: at.left, top: at.top }}
          className="pointer-events-none fixed z-[1000] w-max max-w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-[calc(100%+6px)] rounded-md border bg-popover px-2.5 py-1.5 text-xs leading-snug text-popover-foreground shadow-md"
        >
          {reason}
        </span>, document.body
      )}
    </span>
  );
}
